import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Calendar,
  RefreshCw,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import { useAdminAnalytics } from '../../hooks/useAdminAnalytics';
import EventStreamPanel from '../../components/admin/analytics/EventStreamPanel';
import RevenueStreamPanel from '../../components/admin/analytics/RevenueStreamPanel';
import ComplianceAuditPanel from '../../components/admin/analytics/ComplianceAuditPanel';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminAnalytics = () => {
  const {
    analytics,
    isLoading,
    error,
    period,
    setPeriod,
    periodOptions,
    refetch
  } = useAdminAnalytics('month');

  if (error) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Analytics"
          subtitle="Comprehensive platform analytics and insights"
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
          <p className="text-red-600 dark:text-red-400">Failed to load analytics. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Analytics"
        subtitle="Comprehensive platform analytics and insights"
        actions={
          <>
            <SearchSelect
              value={period}
              onChange={setPeriod}
              options={periodOptions}
              className="w-40"
            />
            <button
              type="button"
              onClick={() => refetch()}
              className="p-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <Link
              to="/admin/advanced-analytics"
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium !rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Advanced Analytics →
            </Link>
          </>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.analytics.title}
        steps={ADMIN_PAGE_GUIDES.analytics.steps}
        icon={ListChecks}
      />

      {isLoading ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading analytics…</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">
                    ${(analytics.overview?.total_revenue || 0).toFixed(2)}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-500" />
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Bookings</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">
                    {analytics.overview?.total_bookings || 0}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-sky-500" />
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Platform Fees</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">
                    ${(analytics.overview?.platform_fees || 0).toFixed(2)}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-primary-500" />
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Completed Bookings</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">
                    {analytics.overview?.completed_bookings || 0}
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-gray-500" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-50 mb-4">Bookings Analytics</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Bookings</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{analytics.bookings_analytics?.total || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Average Value</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    ${(analytics.bookings_analytics?.average_value || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Conversion Rate</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {(analytics.bookings_analytics?.conversion_rate || 0).toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-50 mb-4">Revenue Analytics</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    ${(analytics.revenue_analytics?.total_revenue || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Platform Fees</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    ${(analytics.revenue_analytics?.platform_fees || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Average Transaction</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    ${(analytics.revenue_analytics?.average_transaction || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <EventStreamPanel eventStream={analytics?.event_stream} />
          <RevenueStreamPanel revenueStream={analytics?.revenue_stream} />
          <ComplianceAuditPanel complianceAudit={analytics?.compliance_audit} />

          {analytics.top_performers && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-50 mb-4">Top Performers</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Top Vendors</h3>
                  <div className="space-y-2">
                    {analytics.top_performers.top_vendors?.slice(0, 5).map((vendor, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">{vendor.business_name}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">${parseFloat(vendor.revenue || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Top Services</h3>
                  <div className="space-y-2">
                    {analytics.top_performers.top_services?.slice(0, 5).map((service, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">{service.service_name}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">${parseFloat(service.revenue || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
